import React, { useEffect, useMemo } from 'react';
import { Redirect, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  set,
  getOr,
  size,
  map,
  flow,
  values,
  sortBy,
  get,
  includes,
  reduce,
  forOwn,
  sumBy,
  some,
  isEmpty,
  omit
} from 'lodash/fp';
import { toastr } from 'react-redux-toastr';
import { Content, ButtonToolbar, Button, Icon, Message } from 'rsuite';
import PageLayout from '../../../components/reservations/PageLayout';
import PageHeader from '../../../components/PageHeader';
import ContentLoader from '../../../components/ContentLoader';
import {
  fetchReservationForView,
  fetchReservationPicklists,
  createReservationOrders,
  updateReservationOrders,
  fetchInventoryItemAvailabilityByLocation as fetchInventoryItemAvailability,
  fetchInventoryPackageAvailabilityByLocation as fetchInventoryPackageAvailability
} from '../../../redux/Reservation/sagas/routines';
import {
  getHasLinkedOrderItems,
  getIsFetching,
  getViewReservationData
} from '../../../redux/Reservation/selectors/routes/view';
import {
  getIsSavingOrders,
  getIsFetchingPicklists,
  getPicklists as getViewReservationPicklists,
  getOrdersByPicklist,
  getItemsAvailability,
  getItemsSumQuantityByUniqKey,
  getItemsInitialSumQuantityByUniqKey,
  getHasLinkedPicklistItems
} from '../../../redux/Reservation/selectors/routes/orders';
import {
  clearOrders,
  onEditOrders,
  updateItemsSumQuantities,
  updateItemsInitialSumQuantities
} from '../../../redux/Reservation/actions/routes/orders';
import * as PERMISSIONS from '../../../constants/permissions/reservation';
import {
  REJECTED,
  CANCELED,
  COMPLETED
} from '../../../constants/reservation/Status';
import excludeZeroLimit from '../../../helpers/excludeZeroLimit';
import toOrdersByPicklist from '../../../helpers/toOrdersByPicklist';
import { viewAccess, modifyAccess } from '../../../helpers/permission';
import pagePathToUrl from '../../../helpers/pagePathToUrl';
import { reservationsViewPath } from '../../Paths';
import AccessDenied from '../../../components/AccessDenied';
import AccessControl from '../../../components/AccessControl';
import Picklist from '../../../components/reservations/Picklist';
import getItemAvailabilityUniqId from '../../../helpers/getItemAvailabilityUniqId';

const someWithKey = some.convert({ cap: false });

const defaultOrder = picklistId => ({
  picklistId,
  notes: '',
  items: []
});

export default () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const loadingReservation = useSelector(getIsFetching);
  const loadingPicklists = useSelector(getIsFetchingPicklists);
  const saving = useSelector(getIsSavingOrders);
  const reservation = useSelector(getViewReservationData);
  const hasLinkedPicklistItems = useSelector(getHasLinkedPicklistItems);
  const hasLinkedOrderItems = useSelector(getHasLinkedOrderItems);
  const picklists = useSelector(getViewReservationPicklists);
  const ordersByPicklist = useSelector(getOrdersByPicklist);
  const itemsAvailabilityByUniqKey = useSelector(getItemsAvailability);
  const itemsSumQuantityByUniqKey = useSelector(getItemsSumQuantityByUniqKey);
  const initialSumQuantityByUniqKey = useSelector(
    getItemsInitialSumQuantityByUniqKey
  );

  const hasExceedInventoryLimit = useMemo(
    () => {
      if (isEmpty(itemsAvailabilityByUniqKey)) return false;

      return someWithKey((itemSumQuantity, key) => {
        const itemAvailability = get(key, itemsAvailabilityByUniqKey);
        if (itemAvailability) {
          const { quantity, onholdQuantity } = itemAvailability;
          const initialSumQuantity = getOr(0, key, initialSumQuantityByUniqKey);
          return (
            itemSumQuantity - initialSumQuantity > quantity - onholdQuantity
          );
        }
        return false;
      }, itemsSumQuantityByUniqKey);
    },
    [itemsSumQuantityByUniqKey, itemsAvailabilityByUniqKey]
  );

  const disabledSaveButton =
    saving || isEmpty(ordersByPicklist) || hasExceedInventoryLimit;

  const sortedPicklists = useMemo(
    () =>
      flow(
        list => excludeZeroLimit(reservation, list),
        sortBy(['position', 'name'])
      )(picklists),
    [picklists, reservation]
  );

  const handleUpdateOrder = order => {
    const { picklistId, items = [] } = order;
    const prevItems = get(`${picklistId}.items`, ordersByPicklist);
    if (!isEmpty(items))
      dispatch(onEditOrders(set(picklistId, order, ordersByPicklist)));
    else dispatch(onEditOrders(omit(picklistId, ordersByPicklist)));

    const hasItemsQuantityChanged =
      sumBy('quantity', items) !== sumBy('quantity', prevItems);

    if (hasItemsQuantityChanged) {
      dispatch(updateItemsSumQuantities(order));
    }
  };

  const handleSaveOrders = () => {
    const body = values(ordersByPicklist);
    const orderExists = size(reservation.orders) > 0;

    if (orderExists) dispatch(updateReservationOrders({ id, body }));
    else dispatch(createReservationOrders({ id, body }));
  };

  const fetchOrderItemsAvailability = orders => {
    const inventorySnapshots = flow(
      reduce((allItems, each) => [...allItems, ...each.items], []),
      reduce((all, { inventorySnapshot }) => {
        if (!inventorySnapshot) return all;
        const uniqKey = getItemAvailabilityUniqId(inventorySnapshot);
        return set(uniqKey, inventorySnapshot, all);
      }, {})
    )(orders);
    forOwn(inventorySnapshot => {
      const itemId = get('item.id', inventorySnapshot);
      const locationId = get('location.id', inventorySnapshot);
      const zoneId = get('zone.id', inventorySnapshot);
      dispatch(
        zoneId
          ? fetchInventoryItemAvailability({ id: itemId, locationId, zoneId })
          : fetchInventoryPackageAvailability({ id: itemId, locationId })
      );
    }, inventorySnapshots);
  };

  useEffect(
    () => {
      if (!reservation) {
        dispatch(fetchReservationForView(id));
        dispatch(fetchReservationPicklists(id));
      } else if (size('orders', reservation)) {
        const { orders } = reservation;
        fetchOrderItemsAvailability(orders);
        dispatch(updateItemsInitialSumQuantities(orders));
        dispatch(onEditOrders(toOrdersByPicklist(orders)));
      }
      return function reset() {
        dispatch(clearOrders());
      };
    },
    [id, reservation]
  );

  const status = get('status', reservation);
  const isCompleted = status === COMPLETED;
  const isRejectedOrCanceled = includes(status, [REJECTED, CANCELED]);
  if (isRejectedOrCanceled || (isCompleted && hasLinkedOrderItems)) {
    const errorMessage = isRejectedOrCanceled
      ? 'Orders for Reservation with status [Rejected, Canceled] are no longer editable.'
      : 'Completed Reservation with linked order items are no longer editable.';
    toastr.error('Access Denied', errorMessage);
    return <Redirect to={pagePathToUrl(reservationsViewPath, { id })} />;
  }

  return (
    <PageLayout>
      <AccessControl
        allowOnlyFor={viewAccess(PERMISSIONS.MODULE)}
        fallback={<AccessDenied />}
      >
        <PageHeader title="Reservation Orders" />
        <Content>
          {loadingReservation || loadingPicklists || !reservation ? (
            <ContentLoader />
          ) : (
            <div>
              {isCompleted &&
                hasLinkedPicklistItems && (
                  <Message
                    type="warning"
                    style={{ marginBottom: 10 }}
                    description="This Reservations is already COMPLETED - Items that are linked to inventory cannot be added or changed."
                  />
                )}
              {map(
                each => (
                  <Picklist
                    key={`${each.trackingMethod.id}-${each.id}}`}
                    picklist={each}
                    order={getOr(
                      defaultOrder(each.id),
                      each.id,
                      ordersByPicklist
                    )}
                    onUpdateOrder={handleUpdateOrder}
                  />
                ),
                sortedPicklists
              )}
              {hasExceedInventoryLimit && (
                <Message
                  type="error"
                  style={{ marginBottom: 10 }}
                  description="Oops… there are errors in this order. Please check your ordered items and adjust accordingly. Once corrected you will be able to save the order."
                />
              )}
              <ButtonToolbar style={{ textAlign: 'right' }}>
                <AccessControl allowOnlyFor={modifyAccess(PERMISSIONS.MODULE)}>
                  <Button
                    disabled={disabledSaveButton}
                    appearance="primary"
                    onClick={handleSaveOrders}
                  >
                    <Icon icon="save" /> {saving ? 'Loading...' : 'Save Orders'}
                  </Button>
                </AccessControl>
              </ButtonToolbar>
            </div>
          )}
        </Content>
      </AccessControl>
    </PageLayout>
  );
};
